import { create } from "zustand";

export type ActionFeedbackTone = "success" | "error" | "warning" | "info";

export interface ActionFeedbackState {
  open: boolean;
  tone: ActionFeedbackTone;
  title: string;
  message: string;
  code?: string;
  sessionExpired?: boolean;
}

/**
 * Cross-cutting UI state.
 *
 * Module-specific filter / pagination state lives inside each page via
 * usePagedResource — keep this store small and global-only.
 */
interface UiStore {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;

  drawerOpen: boolean;
  drawerKey: string | null;
  openDrawer: (key: string) => void;
  closeDrawer: () => void;

  toast: { id: number; tone: "info" | "success" | "danger"; message: string } | null;
  notify: (tone: "info" | "success" | "danger", message: string) => void;
  dismissToast: () => void;

  actionFeedback: ActionFeedbackState | null;
  showActionFeedback: (payload: Omit<ActionFeedbackState, "open">) => void;
  dismissActionFeedback: () => void;

  pageLoading: boolean;
  startPageLoading: () => void;
  stopPageLoading: () => void;

  /**
   * Optional URL the Conversations (Chatwoot) iframe should open at instead of
   * the default inbox view. Used to deep-link into a specific Chatwoot page
   * (e.g. Settings → Inboxes) from elsewhere in the app. Null = default view.
   */
  chatwootTarget: string | null;
  setChatwootTarget: (url: string | null) => void;

isSettingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;
}

export const useUiStore = create<UiStore>((set) => ({
  sidebarCollapsed: true,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),

  drawerOpen: false,
  drawerKey: null,
  openDrawer: (key) => set({ drawerOpen: true, drawerKey: key }),
  closeDrawer: () => set({ drawerOpen: false, drawerKey: null }),

  toast: null,
  notify: (tone, message) =>
    set({ toast: { id: Date.now(), tone, message } }),
  dismissToast: () => set({ toast: null }),

  actionFeedback: null,
  showActionFeedback: (payload) =>
    set({
      actionFeedback: {
        open: true,
        tone: payload.tone,
        title: payload.title,
        message: payload.message,
        code: payload.code,
        sessionExpired: payload.sessionExpired,
      },
    }),
  dismissActionFeedback: () => set({ actionFeedback: null }),

  pageLoading: false,
  startPageLoading: () => set({ pageLoading: true }),
  stopPageLoading: () => set({ pageLoading: false }),

  chatwootTarget: null,
  setChatwootTarget: (url) => set({ chatwootTarget: url }),

isSettingsOpen: false,
  setSettingsOpen: (v) => set({ isSettingsOpen: v }),
}));
